import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { logEvent } from 'firebase/analytics';
import {
  Sparkles,
  Loader2,
  PlusCircle,
  Video,
  Wand2,
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
  Save
} from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import SectionTitle from '../ui/SectionTitle';
import { analytics, functionsInstance, db } from '../../firebase/firebase';

/* ------------------------- limits + helpers ------------------------- */
const MAX_TITLE_CHARS = 87;

const clampTitle = (s = '') => {
  const t = String(s).replace(/\s+/g, ' ').trim();
  if (t.length <= MAX_TITLE_CHARS) return t;
  const slice = t.slice(0, MAX_TITLE_CHARS);
  const cut =
    slice.lastIndexOf(' ') > 0 ? slice.slice(0, slice.lastIndexOf(' ')) : slice;
  return cut.trim();
};

const tokenize = (s = '') =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9+&]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const ensureGradeFromText = (text = '') => {
  const m = String(text).match(
    /\b(?:grade\s*)?(\d{1,2})(?:st|nd|rd|th)?\s*grade\b|\bgrade\s*(\d{1,2})\b/i
  );
  if (m) {
    const n = m[1] || m[2];
    const ord =
      n === '1' ? '1st' : n === '2' ? '2nd' : n === '3' ? '3rd' : `${n}th`;
    return `${ord} Grade`;
  }
  return '';
};

const scoreTitle = (title, required = [], bonus = []) => {
  const t = title.toLowerCase();
  let s = 0;

  // Strong reward for required keywords (grade, main topic)
  required.forEach((k) => {
    if (k && t.includes(k.toLowerCase())) s += 3;
  });

  // Bonus for other keywords
  bonus.forEach((k) => {
    if (k && t.includes(k.toLowerCase())) s += 1;
  });

  // Penalize K-ranges only (e.g. K-5, K-8, K-12) — numeric ranges like "Grades 6-7" are fine and wanted
  if (
    /\bK[-–]?\s*\d|\bK-5\b|\bK-6\b|\bK-8\b|\bK-12\b/i.test(title)
  )
    s -= 4;

  // FIX #1: Removed the +0.5 bonus for fluffy power words ("fun", "engaging",
  // "hands-on", "complete", "mastery", "easy", "simple", "proven").
  // That block was actively sorting fluff-heavy titles to the top of the list.

  // Small bonus for key format/delivery modifiers (these ARE search terms)
  if (
    /\b(no-prep|digital|printable|editable|google slides|boom cards)\b/i.test(t)
  )
    s += 0.5;

  return s;
};

const uniqueByEnding = (titles) => {
  const seen = new Set();
  const out = [];
  for (const t of titles) {
    const key = t.toLowerCase().trim();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(t);
    }
  }
  return out;
};

/* ------------------- PROMPT HELPERS ------------------- */

const extractFeatures = (text) => {
  const features = new Set();
  if (/\bno[-\s]?prep\b/i.test(text)) features.add('No-Prep');
  if (/\bprintable(s)?\b/i.test(text)) features.add('Printable');
  if (/\bdigital\b/i.test(text)) features.add('Digital');
  if (/\bgoogle\s*slides?\b/i.test(text)) features.add('Google Slides');
  if (/\bboom\s*cards?\b/i.test(text)) features.add('Boom Cards');
  if (/\beditable\b/i.test(text)) features.add('Editable');
  return Array.from(features);
};

const extractResourceTypes = (text) => {
  const types = new Set();
  if (/\blesson(s)?\b/i.test(text)) types.add('Lesson');
  if (/\bworksheet(s)?\b/i.test(text)) types.add('Worksheets');
  if (/\bactivit(y|ies)\b/i.test(text)) types.add('Activities');
  if (/\btask\s*cards?\b/i.test(text)) types.add('Task Cards');
  if (/\bassessment|quiz|test\b/i.test(text)) types.add('Assessment');
  if (/\bunit\b/i.test(text)) types.add('Unit');
  if (/\bbundle\b/i.test(text)) types.add('Bundle');
  return Array.from(types);
};

const buildContextFromSnapshot = (formSnapshot) => {
  if (!formSnapshot) return '';

  const bits = [];

  if (formSnapshot.subject) bits.push(`Subject: ${formSnapshot.subject}`);
  if (formSnapshot.gradeLevel || formSnapshot.gradeRange)
    bits.push(
      `Grade / Year: ${formSnapshot.gradeLevel || formSnapshot.gradeRange}`
    );
  if (formSnapshot.resourceType)
    bits.push(`Resource Type: ${formSnapshot.resourceType}`);
  if (formSnapshot.teachingFocus)
    bits.push(`Teaching Focus: ${formSnapshot.teachingFocus}`);

  if (!bits.length) return '';

  return `
ADDITIONAL CONTEXT FROM PRODUCT FORM:
- ${bits.join('\n- ')}
`;
};

/* ---- Grade range, seasonal & full-lesson detectors ---- */

const ordinal = n => n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;

const extractGradeRange = (text = '') => {
  const matches = [...text.matchAll(/\b(?:grade\s*)?(\d{1,2})(?:st|nd|rd|th)?\s*(?:grade)?\b/gi)];
  const nums = [...new Set(matches.map(m => parseInt(m[1], 10)).filter(n => n >= 1 && n <= 12))].sort((a, b) => a - b);
  if (nums.length >= 2) {
    const lo = nums[0], hi = nums[nums.length - 1];
    return {
      range: true, lo, hi,
      label: `Grades ${lo}-${hi}`,
      ordinalRange: `${ordinal(lo)}-${ordinal(hi)} Grade`,
      wrongExample: nums.slice(0, 3).map(n => `${ordinal(n)} Grade`).join(' '),
    };
  } else if (nums.length === 1) {
    return { range: false, lo: nums[0], hi: nums[0], label: `${ordinal(nums[0])} Grade` };
  }
  return null;
};

const SEASONAL_PATTERNS = [
  { pattern: /valentine/i,                        label: "Valentine's Day" },
  { pattern: /easter/i,                           label: "Easter" },
  { pattern: /christmas/i,                        label: "Christmas" },
  { pattern: /halloween/i,                        label: "Halloween" },
  { pattern: /thanksgiving/i,                     label: "Thanksgiving" },
  { pattern: /back[\s-]to[\s-]school/i,           label: "Back to School" },
  { pattern: /end[\s-]of[\s-]year|end of school/i,label: "End of Year" },
  { pattern: /st\.?\s*patrick|saint patrick/i,    label: "St. Patrick's Day" },
  { pattern: /earth\s*day/i,                      label: "Earth Day" },
  { pattern: /mother.?s\s*day/i,                  label: "Mother's Day" },
  { pattern: /father.?s\s*day/i,                  label: "Father's Day" },
  { pattern: /new\s*year/i,                       label: "New Year" },
  { pattern: /cinco\s*de\s*mayo/i,                label: "Cinco de Mayo" },
  { pattern: /100th\s*day/i,                      label: "100th Day of School" },
  { pattern: /spring/i,                           label: "Spring" },
  { pattern: /summer/i,                           label: "Summer" },
  { pattern: /\bfall\b|autumn/i,                  label: "Fall" },
  { pattern: /winter/i,                           label: "Winter" },
];

const detectSeasonal = (text = '') =>
  SEASONAL_PATTERNS.filter(s => s.pattern.test(text)).map(s => s.label);

/**
 * Build the contextual instruction block injected into both prompts.
 * Covers: grade range format, seasonal theme, full-lesson requirement.
 */
const buildContextInstructions = (text = '') => {
  const lines = [];
  const gradeData = extractGradeRange(text);
  const seasonal  = detectSeasonal(text);
  const fullLesson = /full\s+lesson/i.test(text);

  if (gradeData?.range) {
    lines.push(`GRADE RANGE: Grades ${gradeData.lo}–${gradeData.hi} detected.
- ALWAYS write as "${gradeData.label}" or "${gradeData.ordinalRange}" — never list separately like "${gradeData.wrongExample}".
- ✅ Correct: "Fractions Grades 4-6 | Differentiated Worksheets Printable PDF"
- ❌ Wrong:   "4th 5th 6th Grade Fractions Worksheets Printable"`);
  } else if (gradeData) {
    lines.push(`GRADE: ${gradeData.label} detected — include in every title near the front.`);
  }

  if (seasonal.length > 0) {
    lines.push(`SEASONAL THEME: ${seasonal.join(', ')} detected.
- Include the seasonal keyword in at least HALF of the titles.
- Place it after the core topic, e.g. "…Grades 5-6 | Valentine's Day Math Centers Printable"
- Do NOT invent seasonal themes not mentioned above.`);
  }

  if (fullLesson) {
    lines.push(`FULL LESSON: The product is explicitly a "Full Lesson".
- Every title MUST include "Full Lesson" or "Complete Lesson" (choose one, never both).
- Place it after topic + grade, e.g. "…Grades 6-7 | Full Lesson Differentiated Printable"`);
  }

  return lines.length ? `\n--- CONTENT REQUIREMENTS ---\n${lines.join('\n\n')}\n` : '';
};

/**
 * Main title-generation prompt
 * style: 'descriptive' | 'seo-heavy'
 */
const generateTitlePromptString = (userInput, formSnapshot, style, contextInstructions = '') => {
  const userText = (userInput || '').trim();
  const gradePhrase = ensureGradeFromText(userText);
  const detectedFeatures = extractFeatures(userText);
  const detectedTypes = extractResourceTypes(userText);
  const extraContext = buildContextFromSnapshot(formSnapshot);

  const styleBlock =
    style === 'seo-heavy'
      ? `
STYLE MODE: SEO-HEAVY
- Prioritise exact match keywords and long-tail search phrases.
- Titles can feel more direct and keyword-dense, as long as they still read naturally.
- Make sure each title starts very close to the user's main keyword phrase.`
      : `
STYLE MODE: DESCRIPTIVE (STILL SEO)
- Balance SEO with classroom clarity.
- Make it obvious when/where this is used (centers, intervention, morning work, test prep).
- Still include the main keyword and grade near the start, but allow slightly more "teacher voice".`;

  return `You are an expert TPT (Teachers Pay Teachers) SEO copywriter. Every title you write must read like a teacher's actual search query typed into TPT search — NOT a product tagline or ad headline. If a title sounds like marketing copy, it fails.

Your goal is to generate 8–10 highly specific, keyword-rich, and diverse titles for a TPT product designed to dominate search rankings.

DATA
- User's Raw Idea: "${userText}"
- Extracted Grade Level: "${gradePhrase || 'None specified'}"
- Detected Features: ${detectedFeatures.length > 0 ? JSON.stringify(detectedFeatures) : 'None'}
- Detected Resource Types: ${detectedTypes.length > 0 ? JSON.stringify(detectedTypes) : 'None'}
${extraContext}
${styleBlock}
${contextInstructions}
TITLE GENERATION RULES:
1. STRICT CHARACTER LIMIT:
   - Each title MUST be between 70 and 87 characters. This is a hard requirement.
   - Titles under 70 characters are FAILURES and must not be included in the output.
   - Aim for 78–87 characters as your default target.
   - Short titles like "5th Grade Math Addition Worksheets" (35 chars) are completely
     unacceptable — they waste searchable space and will be discarded.
   - If your first attempt is too short, STACK additional context:
     sub-skills, use cases, format specifiers, curriculum alignment, or delivery method.
     Examples of valid stacking:
     "5th Grade Math Addition Worksheets | Multi-Digit & Decimal Practice Printable" (77)
     "5th Grade Addition Worksheets | Differentiated Multi-Digit Practice No-Prep" (76)
     "Multi-Digit Addition 5th Grade Worksheets | Differentiated Practice Printable" (77)
2. KEYWORD FOUNDATION:
   - The "User's Raw Idea" and "Extracted Grade Level" are the core.
   - Every title MUST be highly relevant to this idea.
   - If grade level is specified (like "3rd Grade"), include it in every title.
   - Each title should START with a close version of the main topic keywords from the user's idea.
   - After the core phrase, ALWAYS stack at least 2 of:
     • A sub-skill or related concept
     • A use case (Morning Work, Math Centers, Test Prep, Intervention, Homework)
     • A format specifier (Printable, No-Prep, Google Slides, Digital PDF)
     • A curriculum note (Common Core, TEKS Aligned, Differentiated)
3. TITLE VARIETY (keyword-variation only — no marketing angles):
   Generate a mix of these structures. Do NOT rearrange the same words — vary the keyword phrase itself.
   - Lead with main topic + grade + resource type (e.g. "3rd Grade Division Worksheets | Printable & Digital")
   - Lead with the action/skill (e.g. "Adding and Subtracting Fractions | 4th Grade Practice")
   - Lead with context/use case (e.g. "Math Centers 3rd Grade | Long Division Practice Activities")
   - Lead with a long-tail variant keyword a teacher might actually search for
4. INTELLIGENT MODIFIERS:
   - Use Detected Features and Resource Types as a guide.
   - If no clear type is detected, use strong generic types:
     "Worksheets", "Activities", "Task Cards", "Centers", "Practice", "Review", "Unit".
   - You MAY add 1–2 logical high-value features like "No-Prep", "Printable", "Digital", "Editable".
5. BANNED WORDS (hard stop — never use any of these):
   "Engaging", "Hands-On", "Complete", "Mastery", "Easy-to-Use", "Proven", "Simple",
   "Fun", "Perfect", "Powerful", "Ultimate", "Amazing", "Incredible", "Exciting",
   "Effective", "Success", "Jumpstart", "Unleash", "Magic", "Awesome", "Great",
   "Boost", "Transform", "Empower", "Enhance".
   Titles must read like a high-intent search query, not a sales pitch.
6. SEO EXPANSION:
   - Intelligently add 1–2 related long-tail keywords a teacher might search for.
   - Example: Integers → "Adding and Subtracting Integers", "Number Line Practice"
   - Example: Photosynthesis → "Plant Life Cycle", "Science Vocabulary"
7. GRADE FORMAT:
   - Single grade → "5th Grade" or "Grade 5".
   - Multiple grades → ALWAYS use hyphen range: "Grades 5-7" or "5th-7th Grade".
   - NEVER list grades separately like "5th 6th 7th Grade" — always use the range format.
   - Only use a range if the user's input actually mentions multiple grades.
8. EXTRA RULES:
   - Avoid vague titles like "Great Resource For Your Class".
   - Every title must feel like a real, specific TPT listing a teacher would click on while searching.
   - No emojis in titles.

OUTPUT FORMAT (minified JSON):
{
 "titles":[
   "Title Case Title 1...",
   "Title Case Title 2...",
   "Title Case Title 3...",
   "Title Case Title 4...",
   "Title Case Title 5...",
   "Title Case Title 6...",
   "Title Case Title 7...",
   "Title Case Title 8..."
 ],
 "recommendations":{
   "keywordStrategy":[
     {"keyword":"primary-keyword-from-user", "reason":"This is your core SEO term."},
     {"keyword":"suggested-long-tail-keyword", "reason":"This new term targets teachers searching for a related concept."},
     {"keyword":"another-suggested-keyword", "reason":"Captures searches for an important benefit or feature."}
   ],
   "descriptionHooks":[
     "A compelling, problem-oriented hook for the start of your product description.",
     "A benefit-driven hook you can use to open your description.",
     "A simple question-based hook that grabs teacher attention."
   ]
 },
 "thumbnail":{
   "powerWords":["Proven", "Mastery", "Complete", "Save Time", "No Prep"],
   "benefitPhrases":["Boost Engagement", "Ready to Use", "Differentiation Made Easy"]
 }
}`;
};

/**
 * Prompt to improve a *single* chosen title
 * – preserves teacher phrasing, especially end-tags
 * – tries to push length toward 80–87 characters
 */
const generateImprovePromptString = (currentDraft, formSnapshot) => {
  const extraContext = buildContextFromSnapshot(formSnapshot);
  const contextInstructions = buildContextInstructions(currentDraft);

  return `Act as a TPT product title editor. Every title must read like a teacher's actual search query typed into TPT — not a product tagline or ad copy.

CURRENT DRAFT TITLE:
"${currentDraft}"

${extraContext}
${contextInstructions}
TASK:
Improve this title for SEO and search relevance while keeping the teacher's wording intact.

TREAT THE CURRENT TITLE AS THE BASE:
- Keep all important phrases the teacher has typed, especially any tags at the END
  such as "Printable Worksheets", "Google Slides", "PDF", "Digital", "Task Cards",
  "No-Prep", "Math Centers", etc.
- You MAY lightly reorder phrases for clarity or SEO, and you MAY expand on the idea
  by adding 1–2 tightly related long-tail phrases.
- Do NOT delete useful teacher-provided phrases unless they are duplicated or clearly redundant.
- If you shorten something, replace it with an equally clear version (e.g.
  "Google Slides & PDF" → "Google Slides + PDF"), not by removing it.

LENGTH & CHARACTER STRATEGY:
- If the current draft is SHORTER than 75 characters, EXPAND it by adding a SPECIFIC
  long-tail keyword the teacher hasn't used yet — a related sub-skill, curriculum standard,
  grade-level phrase, or format specifier that a teacher would actually search for.
  Do NOT pad with generic filler like "Differentiated Practice" or "No-Prep Activities"
  unless those exact words are a real, high-value search term for this topic.
  Every added word must target a real search query.
- If it is between 75–87 characters, keep the length roughly the same and
  focus on clarity, keyword order, and natural teacher voice.
- The final title MUST be 87 characters or fewer (hard limit).
- Aim for 80–87 characters when possible, using meaningful, search-friendly terms.

SEO RULES:
1. Keep the main topic keywords and grade level very close to the START of the title.
2. Keep the resource type (worksheets, task cards, centers, unit, bundle, etc.) visible.
3. You MAY add 1–2 long-tail keywords that a teacher might also search for, as long
   as the title still reads naturally.
4. BANNED WORDS — never use: "Engaging", "Mastery", "Complete", "Easy", "Fun",
   "Perfect", "Powerful", "Ultimate", "Amazing", "Effective", "Success",
   "Jumpstart", "Unleash", "Magic", "Awesome", "Boost", "Transform".
5. Pipes (|) ARE allowed. Do NOT use exclamation marks (!).

OUTPUT FORMAT (JSON only):
{
  "improvedTitle": "New Title Here..."
}`;
};

/* --------------------------- component --------------------------- */
const TitleGenerator = ({
  currentUser,
  triggerAuthModal,
  formSnapshot,
  selectedTitle,
  onTitleSelect,
  onSnapshotSaved
}) => {
  const [titleKeywords, setTitleKeywords] = useState('');
  const [generatedTitles, setGeneratedTitles] = useState([]);
  const [seoRecommendations, setSeoRecommendations] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [error, setError] = useState('');
  const [finalTitle, setFinalTitle] = useState('');
  const [titleStyle, setTitleStyle] = useState('descriptive');

  const [copiedTitle, setCopiedTitle] = useState(null);
  const [savingTitle, setSavingTitle] = useState(null);
  const [savedTitle, setSavedTitle] = useState(null);

  useEffect(() => {
    if (selectedTitle && selectedTitle !== finalTitle) {
      setFinalTitle(clampTitle(selectedTitle));
    }
  }, [selectedTitle, finalTitle]);

  const callGemini = httpsCallable(functionsInstance, 'callGeminiAPI');

  const handleCopyTitle = (e, text) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTitle(text);
      setTimeout(() => setCopiedTitle(null), 2000);
    });
  };

  const handleSaveToDrafts = async (e, text) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      triggerAuthModal('Please log in to save drafts.');
      return;
    }

    setSavingTitle(text);
    try {
      const newDraft = {
        saveName: text,
        productName: text,
        gradeLevels: [],
        primarySubject: '',
        selectedDomains: [],
        selectedIncludedItems: [],
        customIncludedItem: '',
        tone: 'Friendly & Professional',
        descriptionLength: 'Medium',
        selectedPedagogicalApproaches: [],
        customKeywords: '',
        additionalContext: '',
        targetAudience: '',
        suggestLearningOutcomes: true,
        relatedProducts: [{ id: Date.now(), url: '' }],
        generatedDescription: '',
        savedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };

      const colRef = collection(db, 'users', currentUser.uid, 'savedDescriptions');
      const docRef = await addDoc(colRef, newDraft);

      setSavedTitle(text);
      setTimeout(() => setSavedTitle(null), 3000);

      if (onSnapshotSaved) {
        onSnapshotSaved({ id: docRef.id, ...newDraft });
      }

      setFinalTitle(text);
      onTitleSelect(text);
    } catch (err) {
      console.error('Error saving draft:', err);
      alert('Failed to save draft. Please check your connection.');
    } finally {
      setSavingTitle(null);
    }
  };

  const handleGenerate = async (isMore = false) => {
    if (!currentUser) {
      triggerAuthModal(
        'Please sign up for a free trial or log in to generate titles.'
      );
      return;
    }

    const userText = (titleKeywords || formSnapshot?.productName || '').trim();

    if (!userText) {
      setError('Please provide a Title/Keywords to start.');
      return;
    }

    setError('');
    if (!isMore) setSeoRecommendations(null);
    setIsGenerating(true);

    // Build grade/seasonal/full-lesson instructions before calling the prompt
    const contextInstructions = buildContextInstructions(userText);
    const gradeData = extractGradeRange(userText);

    const prompt = generateTitlePromptString(userText, formSnapshot, titleStyle, contextInstructions);

    try {
      const result = await callGemini({
        prompt,
        model: 'gemini-2.5-flash-lite',
      });

      const rawText = result.data?.generatedText || '{}';
      const cleanedText = rawText.replace(/```json|```/g, '').trim();

      let parsed = {};
      try {
        parsed = JSON.parse(cleanedText || '{}');
      } catch (e) {
        console.error('JSON parsing error:', e, 'Raw text:', rawText);
        setError(
          'Received an invalid response from the AI. Try simplifying your keywords slightly and try again.'
        );
        setIsGenerating(false);
        return;
      }

      let list = Array.isArray(parsed.titles) ? parsed.titles : [];
      list = list.map(clampTitle).filter(Boolean);

      const gradePhrase = ensureGradeFromText(userText);
      const tokens = tokenize(userText).filter(
        (t) =>
          ![
            'resource', 'lesson', 'worksheet', 'worksheets',
            'activity', 'activities', 'grade', 'st', 'nd', 'rd', 'th',
          ].includes(t) && !/^\d+$/.test(t)
      );

      let cleaned = Array.from(new Set(list))
        // Grade filter: for ranges, accept "Grades X-Y" or either individual grade number;
        // for single grade, keep original behaviour.
        .filter((t) => {
          if (gradeData?.range) {
            const tl = t.toLowerCase();
            // Accept if the title contains both grade numbers, or the range label
            return (
              tl.includes(`grades ${gradeData.lo}-${gradeData.hi}`) ||
              tl.includes(`${ordinal(gradeData.lo)}-${ordinal(gradeData.hi)}`) ||
              (tl.includes(String(gradeData.lo)) && tl.includes(String(gradeData.hi)))
            );
          }
          if (gradePhrase) return t.toLowerCase().includes(gradePhrase.toLowerCase());
          return true;
        })
        .filter((t) =>
          tokens.length === 0
            ? true
            : tokens.some((tok) => t.toLowerCase().includes(tok))
        )
        // Only filter out K-ranges, not numeric grade ranges
        .filter((t) => !/\bK[-–]?\s*\d|\bK-5\b|\bK-6\b|\bK-8\b|\bK-12\b/i.test(t));

      cleaned = uniqueByEnding(cleaned);

      // Raised from 60 → 70: titles under 70 chars waste searchable space
      const MIN_TARGET_LENGTH = 70;
      const preferred = cleaned.filter((t) => t.length >= MIN_TARGET_LENGTH);
      if (preferred.length >= 3) {
        cleaned = preferred;
      }

      const required = [gradePhrase].filter(Boolean);
      const bonus = tokens.slice(0, 3);

      const TARGET_LEN = 80;
      cleaned.sort((a, b) => {
        const scoreDiff =
          scoreTitle(b, required, bonus) - scoreTitle(a, required, bonus);
        if (scoreDiff !== 0) return scoreDiff;
        const lenScoreA = -Math.abs(TARGET_LEN - a.length);
        const lenScoreB = -Math.abs(TARGET_LEN - b.length);
        return lenScoreB - lenScoreA;
      });

      if (cleaned.length > 0) {
        setGeneratedTitles((prev) =>
          isMore ? uniqueByEnding([...prev, ...cleaned]) : cleaned
        );
        if (!isMore) {
          const first = cleaned[0];
          setFinalTitle(first);
          onTitleSelect(first);
        }
      } else {
        setError(
          'No titles matched your core keywords. Try a simpler phrase, like "3rd grade division worksheets".'
        );
      }

      const recs = parsed.recommendations || null;
      const thumb = parsed.thumbnail || null;
      if (!isMore && (recs || thumb)) {
        setSeoRecommendations({ ...(recs || {}), thumbnail: thumb || {} });
      }

      logEvent(analytics, 'generate_titles', {
        num_titles: cleaned.length,
        input_length: userText.length,
        style: titleStyle,
      });
    } catch (err) {
      console.error('Error generating or parsing title response:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImprove = async () => {
    if (!finalTitle.trim()) return;
    setIsImproving(true);
    try {
      const prompt = generateImprovePromptString(finalTitle, formSnapshot);
      const result = await callGemini({
        prompt,
        model: 'gemini-2.5-flash-lite',
      });

      const rawText = result.data?.generatedText || '{}';

      const firstBrace = rawText.indexOf('{');
      const lastBrace = rawText.lastIndexOf('}');

      let cleanedText = rawText;
      if (firstBrace !== -1 && lastBrace !== -1) {
        cleanedText = rawText.slice(firstBrace, lastBrace + 1);
      }

      let parsed = {};
      try {
        parsed = JSON.parse(cleanedText);
      } catch (e) {
        console.error('JSON parsing error (improve):', e, 'Raw text:', rawText);
        setIsImproving(false);
        return;
      }

      if (parsed.improvedTitle) {
        const safeTitle = clampTitle(parsed.improvedTitle);
        setFinalTitle(safeTitle);
        onTitleSelect(safeTitle);
      }
    } catch (err) {
      console.error('Error improving title:', err);
    } finally {
      setIsImproving(false);
    }
  };

  const handleFinalEdit = (e) => {
    let val = e.target.value;
    if (val.length > MAX_TITLE_CHARS) {
      val = val.slice(0, MAX_TITLE_CHARS);
    }
    setFinalTitle(val);
    onTitleSelect(val);
  };

  const charCount = finalTitle.length;
  const isOverLimit = charCount > MAX_TITLE_CHARS;
  const isSweetSpot = charCount >= 75 && charCount <= MAX_TITLE_CHARS;

  return (
    <div className="relative bg-white p-6 sm:p-8 rounded-xl shadow-2xl space-y-6 mb-8">
      <a
        href="https://youtu.be/q2GyjPQVb28"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-6 right-6 flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
      >
        <Video size={14} />
        Watch Tutorial
      </a>

      <SectionTitle icon={Sparkles}>Title &amp; SEO Optimizer</SectionTitle>
      <p className="text-sm text-slate-600 -mt-3 mb-3">
        Generate SEO-rich, specific titles and listing insights from your keywords.
      </p>

      {/* Input + style toggle */}
      <div className="space-y-3">
        <div>
          <label
            htmlFor="titleKeywords"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Your Title or Keywords
          </label>
          <input
            type="text"
            id="titleKeywords"
            value={titleKeywords}
            onChange={(e) => setTitleKeywords(e.target.value)}
            placeholder='e.g., 3rd grade division worksheets (printable & digital centers)'
            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
          />
          <p className="text-xs text-slate-500 mt-1 italic">
            Tip: Include grade, topic, and key features (e.g., &quot;no-prep,&quot;
            &quot;digital&quot;) for the best results. The AI will expand from there.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="text-slate-600">Title style:</span>
          <button
            type="button"
            onClick={() => setTitleStyle('descriptive')}
            className={`px-3 py-1.5 rounded-full border transition-colors ${
              titleStyle === 'descriptive'
                ? 'bg-sky-50 border-sky-500 text-sky-700'
                : 'bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100'
            }`}
          >
            Descriptive (still SEO)
          </button>
          <button
            type="button"
            onClick={() => setTitleStyle('seo-heavy')}
            className={`px-3 py-1.5 rounded-full border transition-colors ${
              titleStyle === 'seo-heavy'
                ? 'bg-sky-50 border-sky-500 text-sky-700'
                : 'bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100'
            }`}
          >
            SEO-heavy
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <button
          type="button"
          onClick={() => handleGenerate(false)}
          disabled={isGenerating}
          className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center justify-center transition-colors"
        >
          {isGenerating && !generatedTitles.length ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-5 w-5 mr-2" />
          )}
          Generate Titles &amp; Insights
        </button>
        {generatedTitles.length > 0 && (
          <button
            type="button"
            onClick={() => handleGenerate(true)}
            disabled={isGenerating}
            className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 disabled:bg-slate-400 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center justify-center transition-colors"
          >
            {isGenerating ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <PlusCircle className="h-5 w-5 mr-2" />
            )}
            Get More Title Ideas
          </button>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 mt-2 flex items-center gap-1.5">
          <AlertCircle size={16} />
          {error}
        </p>
      )}

      {generatedTitles.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
          {/* LEFT COLUMN: Suggested titles + Title Optimizer */}
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-slate-800">
                Suggested Titles
              </h3>
              <p className="text-sm text-slate-500">
                Click a title to start, then optimize it below.
              </p>
              <fieldset className="space-y-2 pt-2">
                <legend className="sr-only">Title suggestions</legend>
                {generatedTitles.map((title) => {
                  const key = `tpt-title-${title}`;
                  const radioName = formSnapshot?.id
                    ? `title-suggestion-${formSnapshot.id}`
                    : 'title-suggestion-main';

                  const isSavingThis = savingTitle === title;
                  const isSavedThis = savedTitle === title;

                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-sky-50 border border-slate-200 transition-colors group"
                    >
                      <label className="flex items-center space-x-3 cursor-pointer flex-grow overflow-hidden">
                        <input
                          type="radio"
                          name={radioName}
                          value={title}
                          checked={finalTitle === title}
                          onChange={() => {
                            const t = clampTitle(title);
                            setFinalTitle(t);
                            onTitleSelect(t);
                          }}
                          className="h-4 w-4 text-sky-600 border-slate-400 focus:ring-sky-500 flex-shrink-0"
                        />
                        <span className="font-medium text-slate-700 text-sm truncate pr-2">
                          {title}
                        </span>
                      </label>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => handleSaveToDrafts(e, title)}
                          disabled={isSavingThis}
                          className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-100 transition-colors"
                          title="Save as draft (Blank Canvas)"
                        >
                          {isSavingThis ? (
                            <Loader2 size={16} className="animate-spin text-blue-600" />
                          ) : isSavedThis ? (
                            <CheckCircle2 size={16} className="text-green-500" />
                          ) : (
                            <Save size={16} />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleCopyTitle(e, title)}
                          className="text-slate-400 hover:text-sky-600 p-1.5 rounded-md hover:bg-sky-100 transition-colors"
                          title="Copy title"
                        >
                          {copiedTitle === title ? (
                            <Check size={16} className="text-green-500" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </fieldset>
            </div>

            {/* Title Optimizer */}
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">
                  Title Optimizer
                </h3>
                <span
                  className={`text-xs font-mono px-2 py-1 rounded-md border ${
                    isOverLimit
                      ? 'bg-red-100 text-red-800 border-red-200'
                      : isSweetSpot
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-slate-100 text-slate-700 border-slate-300'
                  }`}
                >
                  {charCount}/{MAX_TITLE_CHARS}
                </span>
              </div>
              <p className="text-[11px] text-slate-600 mb-2">
                Aim to fill out as much of the {MAX_TITLE_CHARS}-character limit
                as possible with meaningful, keyword-rich text. <br />
                <span className="font-semibold">
                  Note: whatever you leave here is what gets sent into the
                  Description Generator title box.
                </span>
              </p>
              <textarea
                value={finalTitle}
                onChange={handleFinalEdit}
                placeholder="Pick a suggestion above, then tweak it here to maximise the character count with strong keywords…"
                rows={3}
                className={`w-full p-3 border rounded-lg text-sm font-medium shadow-sm resize-none focus:outline-none focus:ring-2 ${
                  isOverLimit
                    ? 'border-red-300 focus:ring-red-200'
                    : 'border-slate-300 focus:ring-sky-500'
                }`}
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="text-[11px] text-slate-600">
                  {isOverLimit ? (
                    <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                      <AlertCircle size={11} />
                      Too long — trim {charCount - MAX_TITLE_CHARS} characters.
                    </span>
                  ) : isSweetSpot ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                      <CheckCircle2 size={11} />
                      Great — you&apos;re close to the full character limit.
                    </span>
                  ) : (
                    'Try to get as close to 87 characters as you can without adding fluff.'
                  )}
                </p>

                {finalTitle.length > 5 && (
                  <button
                    type="button"
                    onClick={handleImprove}
                    disabled={isImproving}
                    className="text-[11px] flex items-center gap-1.5 bg-white hover:bg-sky-50 text-sky-700 font-semibold px-3 py-1.5 rounded-md border border-sky-200 shadow-sm transition-colors"
                  >
                    {isImproving ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Wand2 size={12} />
                    )}
                    Magic Polish
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: SEO & Listing Insights */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-800">
              SEO &amp; Listing Insights
            </h3>
            <p className="text-sm text-slate-500">
              Use these suggestions to strengthen your listing.
            </p>

            {seoRecommendations ? (
              <div className="p-4 bg-slate-50 rounded-lg border space-y-5 mt-1">
                {seoRecommendations.keywordStrategy &&
                  seoRecommendations.keywordStrategy.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-slate-700">
                        Keyword Strategy
                      </h4>
                      <ul className="mt-2 space-y-3 text-sm">
                        {seoRecommendations.keywordStrategy.map((item, i) => (
                          <li key={i} className="leading-snug">
                            <strong className="text-teal-800 block">
                              {item.keyword}
                            </strong>
                            <span className="text-slate-600 block">
                              {item.reason}
                            </span>
                            <span className="block text-[11px] text-slate-500 mt-0.5">
                              Where to use: title, first line of description,
                              thumbnail text, and TPT search tags.
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {seoRecommendations.descriptionHooks &&
                  seoRecommendations.descriptionHooks.length > 0 && (
                    <div className="pt-4 border-t">
                      <h4 className="font-semibold text-slate-700">
                        Description Hook Ideas
                      </h4>
                      <ul className="list-disc list-inside mt-2 text-slate-600 space-y-1 text-sm">
                        {seoRecommendations.descriptionHooks.map((p, i) => (
                          <li key={i} className="italic">
                            &quot;{p}&quot;
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {seoRecommendations.thumbnail && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold text-slate-700">
                      Thumbnail Text Ideas
                    </h4>
                    {seoRecommendations.thumbnail.powerWords &&
                      seoRecommendations.thumbnail.powerWords.length > 0 && (
                        <div className="mt-2">
                          <strong className="text-xs text-slate-600 font-semibold uppercase">
                            Power Words:
                          </strong>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {seoRecommendations.thumbnail.powerWords.map(
                              (p, i) => (
                                <span
                                  key={i}
                                  className="text-sm bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold"
                                >
                                  {p}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {seoRecommendations.thumbnail.benefitPhrases &&
                      seoRecommendations.thumbnail.benefitPhrases.length > 0 && (
                        <div className="mt-3">
                          <strong className="text-xs text-slate-600 font-semibold uppercase">
                            Benefit Phrases:
                          </strong>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {seoRecommendations.thumbnail.benefitPhrases.map(
                              (p, i) => (
                                <span
                                  key={i}
                                  className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full font-semibold"
                                >
                                  {p}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            ) : (
              !isGenerating &&
              generatedTitles.length > 0 && (
                <p className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg text-center">
                  Insights are generated with your first batch of titles.
                </p>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TitleGenerator;